/*
  Progress-bar sweep for cumulative progress-bar dots.

  Every progress-bar skin fills each segment with its own CSS transition,
  keyed off the active class. That breaks down whenever the active page moves
  more than one step before the last fill finishes: a multi-page jump (fast
  swipe, dot click, loop wrap), or the rapid single steps a native-scroll
  slider (the product media gallery) emits as the scroll passes each slide.
  Several segments then fill at once, holes open up, and a reversal mid-way
  can leave a segment stuck half-filled.

  So for cumulative bars this script owns the fill instead. It keeps one fill
  front (a position in segments, 0..count) that eases toward the active page
  every animation frame, and paints each segment as its share of that front:
  --pfill-s = clamp(front - index, 0, 1). The .pfill-js rules in base.css map
  that to scaleX() and switch the skin's own transition off. Only one segment
  is ever partly filled, in either direction, at any speed, and a reversal
  just turns the front around.

  The front is an exponential ease toward the target with tau = 0.18 x the
  skin's fill speed, which reaches 90% in the same ~0.42 x speed as the
  skins' own ease-out curve, so a one-step move looks the same as before.
  Speed 0 (or reduced motion) jumps straight to the target. Active-only bars
  ("Highlight the current slide only") are left to their CSS.

  Covers:
  - .upsell-progress--cumulative (Slider block, upsell blocks + containers,
    and the sections that share the skin)
  - .muc-carousel--progress-cumulative (Multi-use collection)
  - .slider__dots--ed-progress-cumulative (product media gallery)
  - .uprog-counter--cumulative (slideshow-component dots: Slideshow,
    Complementary products, Featured product)
  - .video-progress--cumulative (DB - Product videos #1, Swiper)
*/
(function () {
  var BARS = [
    {
      item: '.splide__pagination__page',
      active: 'is-active',
      container: '.splide__pagination',
      cumulative: '.upsell-progress--cumulative, .muc-carousel--progress-cumulative',
    },
    {
      item: '.slider-counter__link--dots',
      active: 'slider-counter__link--active',
      container: '.slider__dots, .slideshow__control-wrapper',
      cumulative: '.slider__dots--ed-progress-cumulative, .uprog-counter--cumulative',
    },
    {
      item: '.swiper-pagination-bullet',
      active: 'swiper-pagination-bullet-active',
      container: '.swiper-pagination',
      cumulative: '.video-progress--cumulative',
    },
  ];
  var state = new WeakMap();

  function barFor(el) {
    for (var i = 0; i < BARS.length; i++) {
      if (el.matches(BARS[i].item)) return BARS[i];
    }
    return null;
  }

  function visibleItems(container, bar) {
    return Array.prototype.filter.call(container.querySelectorAll(bar.item), function (el) {
      return el.getClientRects().length > 0;
    });
  }

  function fillSpeed(el) {
    var fill = el.querySelector('.dot') || el;
    return parseFloat(getComputedStyle(fill, '::after').transitionDuration) || 0;
  }

  function release(el) {
    el.classList.remove('pfill-js');
    el.style.removeProperty('--pfill-s');
  }

  function paint(st) {
    st.items.forEach(function (el, i) {
      var s = Math.min(1, Math.max(0, st.front - i));
      el.style.setProperty('--pfill-s', s.toFixed(4));
    });
  }

  function tick(st, ts) {
    var dt = st.last ? ts - st.last : 16;
    st.last = ts;
    var gap = st.target - st.front;
    st.front += gap * (1 - Math.exp(-dt / st.tau));
    if (Math.abs(st.target - st.front) < 0.002) st.front = st.target;
    paint(st);
    if (st.front !== st.target) {
      st.raf = requestAnimationFrame(function (t) { tick(st, t); });
    } else {
      st.raf = 0;
      st.last = 0;
    }
  }

  function run(container, bar, prevEl) {
    if (!container.closest(bar.cumulative)) return;
    var list = visibleItems(container, bar);
    var now = -1;
    for (var i = 0; i < list.length; i++) {
      if (list[i].classList.contains(bar.active)) {
        now = i;
        break;
      }
    }
    var target = now + 1;
    var st = state.get(container);
    if (!st) {
      if (!list.length) return;
      // Read the skin's speed before .pfill-js switches its transition off.
      var speed = fillSpeed(list[0]);
      var prev = prevEl ? list.indexOf(prevEl) : -1;
      st = {
        items: [],
        front: prev >= 0 ? prev + 1 : target,
        target: target,
        tau: speed * 180,
        raf: 0,
        last: 0,
      };
      state.set(container, st);
    }

    // Items can change (variant filtering hides gallery dots); hand any that
    // dropped out back to their CSS.
    st.items.forEach(function (el) {
      if (list.indexOf(el) < 0) release(el);
    });
    list.forEach(function (el) {
      if (!el.classList.contains('pfill-js')) el.classList.add('pfill-js');
    });
    st.items = list;
    st.target = target;

    if (!st.tau) {
      st.front = target;
      paint(st);
      return;
    }
    paint(st);
    if (!st.raf && st.front !== st.target) {
      st.raf = requestAnimationFrame(function (t) { tick(st, t); });
    }
  }

  new MutationObserver(function (records) {
    var todo = new Map();
    records.forEach(function (r) {
      var el = r.target;
      if (el.nodeType !== 1) return;
      var bar = barFor(el);
      if (!bar) return;
      var container = el.closest(bar.container);
      if (!container) return;
      var entry = todo.get(container) || { bar: bar, prevEl: null };
      var wasActive = (' ' + (r.oldValue || '') + ' ').indexOf(' ' + bar.active + ' ') > -1;
      if (wasActive && !el.classList.contains(bar.active)) entry.prevEl = el;
      todo.set(container, entry);
    });
    todo.forEach(function (entry, container) {
      run(container, entry.bar, entry.prevEl);
    });
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
    attributeOldValue: true,
    subtree: true,
  });
})();
